import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Check, Pencil } from 'lucide-react'

import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { ApiResponse } from '@/types/api'
import type {
  UserProfile,
  ProgramOfInterest,
  EducationEntry,
  WorkEntry,
  ProjectEntry,
  TestScoreEntry,
  AchievementEntry,
  TestType,
} from '@/types/models'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOTAL_STEPS = 7

const PROGRAMS_OF_INTEREST: ProgramOfInterest[] = [
  'MBA', 'MS', 'PhD', 'MA', 'Executive MBA',
]

const EDUCATION_LEVELS = ["Bachelor's", "Master's", "Doctorate"] as const
const GRADING_SYSTEMS = ['Letter Grade', 'GPA 10', 'GPA 4', 'Percentage'] as const
const JOB_NATURES = ['Full-time', 'Part-time', 'Contract', 'Intern', 'Volunteer'] as const
const PROJECT_ROLES = ['People Manager', 'Project Manager', 'Individual Contributor'] as const
const PROJECT_TYPES = ['In-house', 'Client-facing'] as const
const TEST_TYPES: TestType[] = ['TOEFL', 'IELTS', 'PTE', 'DuoLingo', 'GMAT', 'GRE', 'Executive Assessment']

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const TEST_SECTION_SCORES: Partial<Record<TestType, string[]>> = {
  GMAT: ['Verbal', 'Quant', 'IR', 'AW'],
  GRE: ['Verbal', 'Quant', 'AW'],
  TOEFL: ['Reading', 'Listening', 'Speaking', 'Writing'],
  IELTS: ['Reading', 'Listening', 'Speaking', 'Writing'],
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function currentYear() {
  return new Date().getFullYear()
}

function yearRange(from: number, to: number) {
  const years = []
  for (let y = to; y >= from; y--) years.push(y)
  return years
}

// ---------------------------------------------------------------------------
// StepIndicator
// ---------------------------------------------------------------------------

interface StepIndicatorProps {
  currentStep: number
  lastActiveStep: number
  onStepClick: (step: number) => void
}

function StepIndicator({ currentStep, lastActiveStep, onStepClick }: StepIndicatorProps) {
  const labels = ['Basic Info', 'Education', 'Work', 'Projects', 'Tests', 'Awards', 'Review']
  return (
    <div className="flex items-center gap-0 overflow-x-auto pb-2">
      {labels.map((label, idx) => {
        const step = idx + 1
        const completed = step < currentStep
        const active = step === currentStep
        const clickable = step <= lastActiveStep
        return (
          <div key={step} className="flex items-center">
            <button
              type="button"
              onClick={() => clickable && step !== currentStep && onStepClick(step)}
              disabled={!clickable || step === currentStep}
              aria-current={active ? 'step' : undefined}
              className={cn(
                'flex flex-col items-center gap-1 px-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                clickable && step !== currentStep ? 'cursor-pointer' : 'cursor-default',
              )}
            >
              <div
                className={cn(
                  'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                  completed
                    ? 'bg-brand-green text-white'
                    : active
                    ? 'bg-brand-blue text-white'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {completed ? <Check className="h-4 w-4" /> : step}
              </div>
              <span
                className={cn(
                  'hidden sm:block text-xs',
                  active ? 'font-medium text-foreground' : 'text-muted-foreground',
                )}
              >
                {label}
              </span>
            </button>
            {step < TOTAL_STEPS && (
              <div
                className={cn(
                  'h-0.5 w-8 flex-shrink-0 transition-colors',
                  step < currentStep ? 'bg-brand-green' : 'bg-muted',
                )}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 1: Basic Info
// ---------------------------------------------------------------------------

interface Step1Props {
  profile: UserProfile
  onNext: (data: Partial<UserProfile>) => void
  saving: boolean
}

function Step1({ profile, onNext, saving }: Step1Props) {
  const [name, setName] = useState(profile.name ?? '')
  const [email, setEmail] = useState(profile.email ?? '')
  const [programs, setPrograms] = useState<ProgramOfInterest[]>(profile.programsOfInterest ?? [])

  function toggleProgram(p: ProgramOfInterest) {
    setPrograms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
    )
  }

  function handleNext(e: React.FormEvent) {
    e.preventDefault()
    onNext({ name, email: email || undefined, programsOfInterest: programs, currentStep: 2, lastActiveStep: 1 })
  }

  return (
    <form onSubmit={handleNext} className="space-y-5">
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground">Your Name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Full name"
          className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </label>

      <div>
        <p className="mb-2 text-sm font-medium text-foreground">Programs of Interest</p>
        <div className="flex flex-wrap gap-2">
          {PROGRAMS_OF_INTEREST.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => toggleProgram(p)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                programs.includes(p)
                  ? 'border-brand-blue bg-brand-blue text-white'
                  : 'hover:bg-accent',
              )}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground">
          Preferred Contact Email{' '}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Alternative email for admissions contacts"
          className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </label>

      <button
        type="submit"
        disabled={saving}
        className="w-full rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
      >
        {saving ? 'Saving...' : 'Next'}
      </button>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Step 2: Education
// ---------------------------------------------------------------------------

interface Step2Props {
  profile: UserProfile
  onNext: () => void
  onBack: () => void
}

function EducationForm({
  entry,
  onSave,
  onCancel,
  saving,
}: {
  entry: Partial<EducationEntry>
  onSave: (data: Partial<EducationEntry>) => void
  onCancel: () => void
  saving: boolean
}) {
  const [level, setLevel] = useState<EducationEntry['level']>(entry.level ?? "Bachelor's")
  const [institution, setInstitution] = useState(entry.institutionName ?? '')
  const [course, setCourse] = useState(entry.courseName ?? '')
  const [gradingSystem, setGradingSystem] = useState<EducationEntry['gradingSystem']>(
    entry.gradingSystem ?? 'GPA 4',
  )
  const [grade, setGrade] = useState(entry.grade ?? '')
  const [gradYear, setGradYear] = useState(entry.graduationYear ?? currentYear())

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    onSave({ level, institutionName: institution, courseName: course, gradingSystem, grade, graduationYear: gradYear })
  }

  return (
    <form onSubmit={handleSave} className="rounded-lg border bg-card p-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Level</span>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value as EducationEntry['level'])}
            className="rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {EDUCATION_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Graduation Year</span>
          <select
            value={gradYear}
            onChange={(e) => setGradYear(parseInt(e.target.value, 10))}
            className="rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {yearRange(1980, currentYear() + 6).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-xs font-medium text-muted-foreground">Institution Name</span>
          <input
            type="text"
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            placeholder="University of..."
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-xs font-medium text-muted-foreground">Course / Major</span>
          <input
            type="text"
            value={course}
            onChange={(e) => setCourse(e.target.value)}
            placeholder="Computer Science"
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Grading System</span>
          <select
            value={gradingSystem}
            onChange={(e) => setGradingSystem(e.target.value as EducationEntry['gradingSystem'])}
            className="rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {GRADING_SYSTEMS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Grade</span>
          <input
            type="text"
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            placeholder="e.g. 3.8 or 85%"
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-brand-blue px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border px-3 py-1.5 text-xs hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

function Step2({ profile, onNext, onBack }: Step2Props) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const saveMutation = useMutation({
    mutationFn: (data: { id?: string; entry: Partial<EducationEntry> }) =>
      data.id
        ? api.patch(`/v1/profile/education/${data.id}`, data.entry)
        : api.post('/v1/profile/education', data.entry),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      setEditingId(null)
      setAdding(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/profile/education/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  })

  return (
    <div className="space-y-4">
      {profile.education.map((edu) =>
        editingId === edu.id ? (
          <EducationForm
            key={edu.id}
            entry={edu}
            onSave={(data) => saveMutation.mutate({ id: edu.id, entry: data })}
            onCancel={() => setEditingId(null)}
            saving={saveMutation.isPending}
          />
        ) : (
          <div key={edu.id} className="flex items-start justify-between rounded-lg border bg-card p-4">
            <div>
              <p className="font-medium text-foreground">{edu.institutionName}</p>
              <p className="text-sm text-muted-foreground">
                {edu.level} in {edu.courseName}
              </p>
              <p className="text-xs text-muted-foreground">
                {edu.gradingSystem}: {edu.grade} &middot; {edu.graduationYear}
              </p>
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setEditingId(edu.id)}
                className="rounded p-1 text-muted-foreground hover:bg-accent"
                aria-label="Edit"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(edu.id)}
                className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive"
                aria-label="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ),
      )}

      {adding ? (
        <EducationForm
          entry={{}}
          onSave={(data) => saveMutation.mutate({ entry: data })}
          onCancel={() => setAdding(false)}
          saving={saveMutation.isPending}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex w-full items-center gap-2 rounded-lg border border-dashed px-4 py-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <Plus className="h-4 w-4" />
          Add Education
        </button>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 rounded-md border px-4 py-2 text-sm hover:bg-accent"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="flex-1 rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
        >
          Next
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 3: Work Experience
// ---------------------------------------------------------------------------

function WorkForm({
  entry,
  onSave,
  onCancel,
  saving,
}: {
  entry: Partial<WorkEntry>
  onSave: (data: Partial<WorkEntry>) => void
  onCancel: () => void
  saving: boolean
}) {
  const [position, setPosition] = useState(entry.position ?? '')
  const [org, setOrg] = useState(entry.organization ?? '')
  const [nature, setNature] = useState<WorkEntry['natureOfJob']>(entry.natureOfJob ?? 'Full-time')
  const [startMonth, setStartMonth] = useState(entry.startMonth ?? 'January')
  const [startYear, setStartYear] = useState(entry.startYear ?? currentYear())
  const [isCurrent, setIsCurrent] = useState(entry.isCurrent ?? false)
  const [endMonth, setEndMonth] = useState(entry.endMonth ?? 'January')
  const [endYear, setEndYear] = useState(entry.endYear ?? currentYear())
  const [description, setDescription] = useState(entry.description ?? '')

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    onSave({
      position, organization: org, natureOfJob: nature, startMonth, startYear,
      isCurrent,
      endMonth: isCurrent ? undefined : endMonth,
      endYear: isCurrent ? undefined : endYear,
      description: description || undefined,
    })
  }

  return (
    <form onSubmit={handleSave} className="rounded-lg border bg-card p-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Position</span>
          <input type="text" value={position} onChange={(e) => setPosition(e.target.value)}
            placeholder="Software Engineer"
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Organization</span>
          <input type="text" value={org} onChange={(e) => setOrg(e.target.value)}
            placeholder="Company / Org name"
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Nature</span>
          <select value={nature} onChange={(e) => setNature(e.target.value as WorkEntry['natureOfJob'])}
            className="rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {JOB_NATURES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Start</span>
          <div className="flex gap-2">
            <select value={startMonth} onChange={(e) => setStartMonth(e.target.value)}
              className="flex-1 rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={startYear} onChange={(e) => setStartYear(parseInt(e.target.value, 10))}
              className="w-24 rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              {yearRange(1990, currentYear()).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-col gap-1 sm:col-span-2">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isCurrent} onChange={(e) => setIsCurrent(e.target.checked)} />
            <span className="text-muted-foreground">Currently working here</span>
          </label>
        </div>
        {!isCurrent && (
          <div className="flex flex-col gap-1 sm:col-span-2">
            <span className="text-xs font-medium text-muted-foreground">End</span>
            <div className="flex gap-2">
              <select value={endMonth} onChange={(e) => setEndMonth(e.target.value)}
                className="flex-1 rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={endYear} onChange={(e) => setEndYear(parseInt(e.target.value, 10))}
                className="w-24 rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {yearRange(1990, currentYear() + 2).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        )}
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-xs font-medium text-muted-foreground">Description</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)}
            rows={2} placeholder="Key responsibilities and achievements..."
            className="resize-none rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving}
          className="rounded-md bg-brand-blue px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onCancel}
          className="rounded-md border px-3 py-1.5 text-xs hover:bg-accent">Cancel</button>
      </div>
    </form>
  )
}

function Step3({ profile, onNext, onBack }: { profile: UserProfile; onNext: () => void; onBack: () => void }) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const saveMutation = useMutation({
    mutationFn: (data: { id?: string; entry: Partial<WorkEntry> }) =>
      data.id
        ? api.patch(`/v1/profile/work-experience/${data.id}`, data.entry)
        : api.post('/v1/profile/work-experience', data.entry),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['profile'] }); setEditingId(null); setAdding(false) },
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/profile/work-experience/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  })

  return (
    <div className="space-y-4">
      {profile.workExperience.map((work) =>
        editingId === work.id ? (
          <WorkForm key={work.id} entry={work}
            onSave={(data) => saveMutation.mutate({ id: work.id, entry: data })}
            onCancel={() => setEditingId(null)} saving={saveMutation.isPending} />
        ) : (
          <div key={work.id} className="flex items-start justify-between rounded-lg border bg-card p-4">
            <div>
              <p className="font-medium text-foreground">{work.position}</p>
              <p className="text-sm text-muted-foreground">{work.organization} &middot; {work.natureOfJob}</p>
              <p className="text-xs text-muted-foreground">
                {work.startMonth} {work.startYear} — {work.isCurrent ? 'Present' : `${work.endMonth} ${work.endYear}`}
              </p>
            </div>
            <div className="flex gap-1">
              <button type="button" onClick={() => setEditingId(work.id)}
                className="rounded p-1 text-muted-foreground hover:bg-accent" aria-label="Edit">
                <Pencil className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => deleteMutation.mutate(work.id)}
                className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive" aria-label="Remove">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ),
      )}
      {adding ? (
        <WorkForm entry={{}} onSave={(data) => saveMutation.mutate({ entry: data })}
          onCancel={() => setAdding(false)} saving={saveMutation.isPending} />
      ) : (
        <button type="button" onClick={() => setAdding(true)}
          className="flex w-full items-center gap-2 rounded-lg border border-dashed px-4 py-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground">
          <Plus className="h-4 w-4" /> Add Work Experience
        </button>
      )}
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onBack} className="flex-1 rounded-md border px-4 py-2 text-sm hover:bg-accent">Back</button>
        <button type="button" onClick={onNext} className="flex-1 rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90">Next</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 4: Projects
// ---------------------------------------------------------------------------

function ProjectForm({
  entry, onSave, onCancel, saving,
}: { entry: Partial<ProjectEntry>; onSave: (d: Partial<ProjectEntry>) => void; onCancel: () => void; saving: boolean }) {
  const [title, setTitle] = useState(entry.title ?? '')
  const [duration, setDuration] = useState(entry.duration ?? '')
  const [role, setRole] = useState<ProjectEntry['role']>(entry.role ?? 'Individual Contributor')
  const [teamSize, setTeamSize] = useState<number | ''>(entry.teamSize ?? '')
  const [projectType, setProjectType] = useState<ProjectEntry['projectType']>(entry.projectType ?? 'In-house')
  const [description, setDescription] = useState(entry.description ?? '')

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    onSave({ title, duration, role, teamSize: teamSize !== '' ? Number(teamSize) : undefined, projectType, description: description || undefined })
  }
  return (
    <form onSubmit={handleSave} className="rounded-lg border bg-card p-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-xs font-medium text-muted-foreground">Project Title</span>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Project name"
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Duration</span>
          <input type="text" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g. 6 months"
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as ProjectEntry['role'])}
            className="rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {PROJECT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Team Size (optional)</span>
          <input type="number" value={teamSize} onChange={(e) => setTeamSize(e.target.value ? parseInt(e.target.value, 10) : '')} min={1}
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Project Type</span>
          <select value={projectType} onChange={(e) => setProjectType(e.target.value as ProjectEntry['projectType'])}
            className="rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-xs font-medium text-muted-foreground">Description</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
            className="resize-none rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="rounded-md bg-brand-blue px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border px-3 py-1.5 text-xs hover:bg-accent">Cancel</button>
      </div>
    </form>
  )
}

function Step4({ profile, onNext, onBack }: { profile: UserProfile; onNext: () => void; onBack: () => void }) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const saveMutation = useMutation({
    mutationFn: (data: { id?: string; entry: Partial<ProjectEntry> }) =>
      data.id ? api.patch(`/v1/profile/projects/${data.id}`, data.entry) : api.post('/v1/profile/projects', data.entry),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['profile'] }); setEditingId(null); setAdding(false) },
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/profile/projects/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  })

  return (
    <div className="space-y-4">
      {profile.projects.map((proj) =>
        editingId === proj.id ? (
          <ProjectForm key={proj.id} entry={proj}
            onSave={(data) => saveMutation.mutate({ id: proj.id, entry: data })}
            onCancel={() => setEditingId(null)} saving={saveMutation.isPending} />
        ) : (
          <div key={proj.id} className="flex items-start justify-between rounded-lg border bg-card p-4">
            <div>
              <p className="font-medium text-foreground">{proj.title}</p>
              <p className="text-sm text-muted-foreground">{proj.role} &middot; {proj.projectType}</p>
              <p className="text-xs text-muted-foreground">{proj.duration}{proj.teamSize ? ` &middot; Team: ${proj.teamSize}` : ''}</p>
            </div>
            <div className="flex gap-1">
              <button type="button" onClick={() => setEditingId(proj.id)} className="rounded p-1 text-muted-foreground hover:bg-accent"><Pencil className="h-4 w-4" /></button>
              <button type="button" onClick={() => deleteMutation.mutate(proj.id)} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ),
      )}
      {adding ? (
        <ProjectForm entry={{}} onSave={(data) => saveMutation.mutate({ entry: data })} onCancel={() => setAdding(false)} saving={saveMutation.isPending} />
      ) : (
        <button type="button" onClick={() => setAdding(true)} className="flex w-full items-center gap-2 rounded-lg border border-dashed px-4 py-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground">
          <Plus className="h-4 w-4" /> Add Project
        </button>
      )}
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onBack} className="flex-1 rounded-md border px-4 py-2 text-sm hover:bg-accent">Back</button>
        <button type="button" onClick={onNext} className="flex-1 rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90">Next</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 5: Test Scores
// ---------------------------------------------------------------------------

function TestScoreForm({
  entry, onSave, onCancel, saving, workspaceId,
}: { entry: Partial<TestScoreEntry>; onSave: (d: Partial<TestScoreEntry>) => void; onCancel: () => void; saving: boolean; workspaceId: string }) {
  const [testType, setTestType] = useState<TestType>(entry.testType ?? 'GMAT')
  const [testDate, setTestDate] = useState(entry.testDate?.slice(0, 10) ?? '')
  const [totalScore, setTotalScore] = useState<number | ''>(entry.totalScore ?? '')
  const [sections, setSections] = useState<Record<string, number>>(entry.sectionScores ?? {})

  const sectionKeys = TEST_SECTION_SCORES[testType] ?? []

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    onSave({
      testType, testDate: testDate || undefined, totalScore: Number(totalScore),
      sectionScores: sectionKeys.length > 0 ? sections : undefined,
    })
  }

  return (
    <form onSubmit={handleSave} className="rounded-lg border bg-card p-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Test Type</span>
          <select value={testType} onChange={(e) => { setTestType(e.target.value as TestType); setSections({}) }}
            className="rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {TEST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Test Date (optional)</span>
          <input type="date" value={testDate} onChange={(e) => setTestDate(e.target.value)}
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Total Score</span>
          <input type="number" value={totalScore} onChange={(e) => setTotalScore(e.target.value ? parseFloat(e.target.value) : '')}
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
        {sectionKeys.map((key) => (
          <label key={key} className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">{key}</span>
            <input type="number" value={sections[key] ?? ''}
              onChange={(e) => setSections((prev) => ({ ...prev, [key]: parseFloat(e.target.value) }))}
              className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="rounded-md bg-brand-blue px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border px-3 py-1.5 text-xs hover:bg-accent">Cancel</button>
      </div>
    </form>
  )
}

function Step5({ profile, onNext, onBack, workspaceId }: { profile: UserProfile; onNext: () => void; onBack: () => void; workspaceId: string }) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const saveMutation = useMutation({
    mutationFn: (data: { id?: string; entry: Partial<TestScoreEntry> }) =>
      data.id ? api.patch(`/v1/profile/test-scores/${data.id}`, data.entry) : api.post('/v1/profile/test-scores', data.entry),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['profile'] }); setEditingId(null); setAdding(false) },
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/profile/test-scores/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  })
  const uploadMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const fd = new FormData()
      fd.append('file', file)
      return api.upload(`/v1/profile/test-scores/${id}/upload`, fd)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  })

  return (
    <div className="space-y-4">
      {profile.testScores.map((ts) =>
        editingId === ts.id ? (
          <TestScoreForm key={ts.id} entry={ts}
            onSave={(data) => saveMutation.mutate({ id: ts.id, entry: data })}
            onCancel={() => setEditingId(null)} saving={saveMutation.isPending} workspaceId={workspaceId} />
        ) : (
          <div key={ts.id} className="flex items-start justify-between rounded-lg border bg-card p-4">
            <div>
              <p className="font-medium text-foreground">{ts.testType}</p>
              <p className="text-sm text-muted-foreground">Total: {ts.totalScore}</p>
              {ts.sectionScores && Object.entries(ts.sectionScores).length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {Object.entries(ts.sectionScores).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                </p>
              )}
              {ts.reportFileName && (
                <p className="text-xs text-muted-foreground mt-1">Report: {ts.reportFileName}</p>
              )}
              <label className="mt-2 flex items-center gap-2 text-xs text-brand-blue cursor-pointer">
                <input type="file" accept=".pdf" className="sr-only"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) uploadMutation.mutate({ id: ts.id, file })
                    e.target.value = ''
                  }} />
                {uploadMutation.isPending ? 'Uploading...' : 'Upload Report'}
              </label>
            </div>
            <div className="flex gap-1">
              <button type="button" onClick={() => setEditingId(ts.id)} className="rounded p-1 text-muted-foreground hover:bg-accent"><Pencil className="h-4 w-4" /></button>
              <button type="button" onClick={() => deleteMutation.mutate(ts.id)} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ),
      )}
      {adding ? (
        <TestScoreForm entry={{}} onSave={(data) => saveMutation.mutate({ entry: data })} onCancel={() => setAdding(false)} saving={saveMutation.isPending} workspaceId={workspaceId} />
      ) : (
        <button type="button" onClick={() => setAdding(true)} className="flex w-full items-center gap-2 rounded-lg border border-dashed px-4 py-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground">
          <Plus className="h-4 w-4" /> Add Test Score
        </button>
      )}
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onBack} className="flex-1 rounded-md border px-4 py-2 text-sm hover:bg-accent">Back</button>
        <button type="button" onClick={onNext} className="flex-1 rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90">Next</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 6: Achievements
// ---------------------------------------------------------------------------

function AchievementForm({ entry, onSave, onCancel, saving }: { entry: Partial<AchievementEntry>; onSave: (d: Partial<AchievementEntry>) => void; onCancel: () => void; saving: boolean }) {
  const [title, setTitle] = useState(entry.title ?? '')
  const [issuer, setIssuer] = useState(entry.issuer ?? '')
  const [year, setYear] = useState<number | ''>(entry.year ?? '')
  const [description, setDescription] = useState(entry.description ?? '')

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    onSave({ title, issuer: issuer || undefined, year: year !== '' ? Number(year) : undefined, description: description || undefined })
  }

  return (
    <form onSubmit={handleSave} className="rounded-lg border bg-card p-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-xs font-medium text-muted-foreground">Title *</span>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Award or achievement name"
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Issuer</span>
          <input type="text" value={issuer} onChange={(e) => setIssuer(e.target.value)} placeholder="Organization"
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Year</span>
          <input type="number" value={year} onChange={(e) => setYear(e.target.value ? parseInt(e.target.value, 10) : '')} min={1950} max={currentYear() + 1}
            className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
        <label className="flex flex-col gap-1 sm:col-span-2">
          <span className="text-xs font-medium text-muted-foreground">Description (optional)</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
            className="resize-none rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="rounded-md bg-brand-blue px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border px-3 py-1.5 text-xs hover:bg-accent">Cancel</button>
      </div>
    </form>
  )
}

function Step6({ profile, onNext, onBack }: { profile: UserProfile; onNext: () => void; onBack: () => void }) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const saveMutation = useMutation({
    mutationFn: (data: { id?: string; entry: Partial<AchievementEntry> }) =>
      data.id ? api.patch(`/v1/profile/achievements/${data.id}`, data.entry) : api.post('/v1/profile/achievements', data.entry),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['profile'] }); setEditingId(null); setAdding(false) },
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/profile/achievements/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  })

  return (
    <div className="space-y-4">
      {profile.achievements.map((ach) =>
        editingId === ach.id ? (
          <AchievementForm key={ach.id} entry={ach}
            onSave={(data) => saveMutation.mutate({ id: ach.id, entry: data })}
            onCancel={() => setEditingId(null)} saving={saveMutation.isPending} />
        ) : (
          <div key={ach.id} className="flex items-start justify-between rounded-lg border bg-card p-4">
            <div>
              <p className="font-medium text-foreground">{ach.title}</p>
              {ach.issuer && <p className="text-sm text-muted-foreground">{ach.issuer}{ach.year ? ` · ${ach.year}` : ''}</p>}
            </div>
            <div className="flex gap-1">
              <button type="button" onClick={() => setEditingId(ach.id)} className="rounded p-1 text-muted-foreground hover:bg-accent"><Pencil className="h-4 w-4" /></button>
              <button type="button" onClick={() => deleteMutation.mutate(ach.id)} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ),
      )}
      {adding ? (
        <AchievementForm entry={{}} onSave={(data) => saveMutation.mutate({ entry: data })} onCancel={() => setAdding(false)} saving={saveMutation.isPending} />
      ) : (
        <button type="button" onClick={() => setAdding(true)} className="flex w-full items-center gap-2 rounded-lg border border-dashed px-4 py-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground">
          <Plus className="h-4 w-4" /> Add Achievement
        </button>
      )}
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onBack} className="flex-1 rounded-md border px-4 py-2 text-sm hover:bg-accent">Back</button>
        <button type="button" onClick={onNext} className="flex-1 rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90">Next</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 7: Review
// ---------------------------------------------------------------------------

function Step7({ profile, onEditStep, patchProfile }: { profile: UserProfile; onEditStep: (step: number) => void; patchProfile: (data: Partial<UserProfile>) => void }) {
  const completedRef = useRef(false)

  // Calculate completion
  const dataPoints = [
    profile.name || profile.programsOfInterest?.length,
    profile.education.length > 0,
    profile.workExperience.length > 0,
    profile.projects.length > 0,
    profile.testScores.length > 0,
    profile.achievements.length > 0,
    true, // step 7 itself
  ]
  const completedCount = dataPoints.filter(Boolean).length
  const percentage = Math.round((completedCount / TOTAL_STEPS) * 100)

  useEffect(() => {
    if (!completedRef.current && !profile.formCompletedAt && completedCount === TOTAL_STEPS) {
      completedRef.current = true
      patchProfile({ formCompletedAt: new Date().toISOString() })
    }
  }, [completedCount, profile.formCompletedAt, patchProfile])

  function SectionRow({ label, step, hasData }: { label: string; step: number; hasData: boolean }) {
    return (
      <div className="flex items-center justify-between py-2 border-b last:border-0">
        <div className="flex items-center gap-3">
          <span className={cn('h-2 w-2 rounded-full', hasData ? 'bg-brand-green' : 'bg-muted')} />
          <span className="text-sm text-foreground">{label}</span>
        </div>
        <button type="button" onClick={() => onEditStep(step)} className="text-xs text-brand-blue hover:underline">
          Edit
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Completion bar */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-foreground">Profile Completion</span>
          <span className="text-sm font-semibold text-brand-blue">{percentage}%</span>
        </div>
        <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-brand-green transition-all duration-500" style={{ width: `${percentage}%` }} />
        </div>
        {percentage === 100 && (
          <div className="mt-3 rounded-lg bg-brand-green/10 px-4 py-3 text-center text-sm font-semibold text-brand-green">
            Your profile is complete!
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="rounded-xl border bg-card divide-y">
        <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Summary
        </div>
        <SectionRow label={`Basic Info${profile.name ? ` — ${profile.name}` : ''}`} step={1} hasData={!!(profile.name || profile.programsOfInterest?.length)} />
        <SectionRow label={`Education — ${profile.education.length} entr${profile.education.length === 1 ? 'y' : 'ies'}`} step={2} hasData={profile.education.length > 0} />
        <SectionRow label={`Work Experience — ${profile.workExperience.length} entr${profile.workExperience.length === 1 ? 'y' : 'ies'}`} step={3} hasData={profile.workExperience.length > 0} />
        <SectionRow label={`Projects — ${profile.projects.length} entr${profile.projects.length === 1 ? 'y' : 'ies'}`} step={4} hasData={profile.projects.length > 0} />
        <SectionRow label={`Test Scores — ${profile.testScores.length} entr${profile.testScores.length === 1 ? 'y' : 'ies'}`} step={5} hasData={profile.testScores.length > 0} />
        <SectionRow label={`Awards — ${profile.achievements.length} entr${profile.achievements.length === 1 ? 'y' : 'ies'}`} step={6} hasData={profile.achievements.length > 0} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ProfilePage
// ---------------------------------------------------------------------------

/**
 * ProfilePage — 7-step multi-step form for applicant profile data.
 *
 * Resumes from lastActiveStep if partially complete.
 * Step 7 is a read-only review with completion tracking.
 */
export default function ProfilePage() {
  const queryClient = useQueryClient()
  const { currentWorkspaceId } = useWorkspaceStore()
  const [currentStep, setCurrentStep] = useState(1)

  const { data, isLoading } = useQuery<ApiResponse<UserProfile>>({
    queryKey: ['profile'],
    queryFn: () => api.get<ApiResponse<UserProfile>>('/v1/profile'),
    enabled: !!currentWorkspaceId,
  })
  const profile = data?.data

  // Resume from lastActiveStep on initial load
  const resumedRef = useRef(false)
  useEffect(() => {
    if (!resumedRef.current && profile && profile.lastActiveStep > 1) {
      resumedRef.current = true
      setCurrentStep(profile.currentStep ?? profile.lastActiveStep)
    }
  }, [profile])

  const patchMutation = useMutation({
    mutationFn: (data: Partial<UserProfile>) => api.patch('/v1/profile', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  })

  function goToStep(step: number) {
    setCurrentStep(step)
  }

  function handleStep1Next(data: Partial<UserProfile>) {
    patchMutation.mutate(data, {
      onSuccess: () => setCurrentStep(2),
    })
  }

  function handleStepNext(nextStep: number) {
    patchMutation.mutate({ currentStep: nextStep, lastActiveStep: currentStep }, {
      onSuccess: () => setCurrentStep(nextStep),
    })
  }

  const STEP_TITLES = [
    'Basic Info',
    'Education History',
    'Work Experience',
    'Projects',
    'Test Scores',
    'Awards & Achievements',
    'Review',
  ]

  if (isLoading || !profile) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Loading profile...
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">My Profile</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Complete your profile to get personalized recommendations.
        </p>
      </div>

      {/* Step indicator */}
      <div className="mb-8">
        <StepIndicator
          currentStep={currentStep}
          lastActiveStep={Math.max(profile.lastActiveStep ?? 1, currentStep)}
          onStepClick={goToStep}
        />
      </div>

      {/* Step title */}
      <h2 className="mb-5 text-lg font-semibold text-foreground">
        Step {currentStep}: {STEP_TITLES[currentStep - 1]}
      </h2>

      {/* Step content */}
      {currentStep === 1 && (
        <Step1 profile={profile} onNext={handleStep1Next} saving={patchMutation.isPending} />
      )}
      {currentStep === 2 && (
        <Step2 profile={profile} onNext={() => handleStepNext(3)} onBack={() => goToStep(1)} />
      )}
      {currentStep === 3 && (
        <Step3 profile={profile} onNext={() => handleStepNext(4)} onBack={() => goToStep(2)} />
      )}
      {currentStep === 4 && (
        <Step4 profile={profile} onNext={() => handleStepNext(5)} onBack={() => goToStep(3)} />
      )}
      {currentStep === 5 && (
        <Step5 profile={profile} onNext={() => handleStepNext(6)} onBack={() => goToStep(4)} workspaceId={currentWorkspaceId ?? ''} />
      )}
      {currentStep === 6 && (
        <Step6 profile={profile} onNext={() => handleStepNext(7)} onBack={() => goToStep(5)} />
      )}
      {currentStep === 7 && (
        <Step7
          profile={profile}
          onEditStep={(step) => goToStep(step)}
          patchProfile={(data) => patchMutation.mutate(data)}
        />
      )}
    </div>
  )
}
